import { type ReactNode, useState } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

import { errorCopy } from '@/content';

import { useReporter } from './observability/ReportingContext';

export type RouteErrorScreenProps = {
  /** Rendered inside a layout that already provides `<main>` (the signed-in layout), so it doesn't add its own. */
  inLayout?: boolean | undefined;
};

/**
 * React Router renders this for routing and loader errors, and, inside the signed-in layout, for a screen's render
 * errors. A 404 is a product screen (stale links happen) and is not reported; anything else is reported and gets the
 * same shape as ScreenErrorBoundary's fallback: generic copy (the raw error is for the reporter, never the user)
 * and the reference id.
 */
export const RouteErrorScreen = ({ inLayout = false }: RouteErrorScreenProps) => {
  const error = useRouteError();
  const reporter = useReporter();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  // Report once, when the screen mounts for this error. A lazy initializer keeps it out of render-time effects.
  const [referenceId] = useState(() => (notFound ? undefined : reporter.report(error, { source: 'router' })));
  const frame = (content: ReactNode) => (inLayout ? content : <main>{content}</main>);

  if (notFound) {
    return frame(
      <>
        <h1>{errorCopy.notFound.title}</h1>
        <p>{errorCopy.notFound.body}</p>
        <Link to="/">{errorCopy.notFound.home}</Link>
      </>,
    );
  }

  return frame(
    <>
      <h1>{errorCopy.unexpected.title}</h1>
      <p role="alert">{errorCopy.unexpected.body}</p>
      {referenceId !== undefined && (
        <p>
          {errorCopy.unexpected.referenceLabel}: {referenceId}
        </p>
      )}
      <Link to="/">{errorCopy.notFound.home}</Link>
    </>,
  );
};
